import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import { NavLink } from 'react-router-dom';
import AutorenewIcon from '@material-ui/icons/Autorenew';
import AssessmentIcon from '@material-ui/icons/Assessment';
import LayersIcon from '@material-ui/icons/Layers';
import TableChartIcon from '@material-ui/icons/TableChart';
import shopifyLogo from '../../assets/images/shopify.png';
import salesforceLogo from '../../assets/images/salesforce.jpg';
import facebookLogo from '../../assets/images/facebook.jpg';
import googlesheetsLogo from '../../assets/images/googlesheets.jpg';
import excelLogo from '../../assets/images/excel.png';
import { preserveTrackingParams } from '../../utils/hubspotTracking';

const ROTATE_MS = 5000;
const TRANSITION_MS = 280;

function FlowConnector({ flipped, reduceMotion, uid }) {
    const w = 72;
    const h = 40;
    const y = 20;
    const x1 = flipped ? w - 4 : 4;
    const x2 = flipped ? 4 : w - 4;
    const path = `M${x1},${y} L${x2},${y}`;
    const gradId = `flow-line-grad-${uid}`;
    const filterId = `flow-dot-glow-${uid}`;
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="home-hero-flow-svg" aria-hidden style={{ flexShrink: 0, overflow: 'visible' }}>
            <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="rgba(99, 102, 241, 0.15)" />
                    <stop offset="45%" stopColor="rgba(56, 189, 248, 0.75)" />
                    <stop offset="100%" stopColor="rgba(14, 165, 233, 0.35)" />
                </linearGradient>
                <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>
            <line
                x1={x1}
                y1={y}
                x2={x2}
                y2={y}
                stroke={`url(#${gradId})`}
                strokeWidth="2"
                strokeLinecap="round"
                opacity="0.85"
            />
            {!reduceMotion ? (
                <circle r="3.5" fill="#a5f3fc" filter={`url(#${filterId})`}>
                    <animateMotion dur="2.4s" repeatCount="indefinite" path={path} />
                </circle>
            ) : null}
            {!reduceMotion ? (
                <circle r="2.5" fill="#38bdf8" opacity="0.85">
                    <animateMotion dur="2.4s" repeatCount="indefinite" begin="0.9s" path={path} />
                </circle>
            ) : null}
        </svg>
    );
}

/** Vertical connector for stacked mobile layout */
function FlowConnectorVertical({ reduceMotion, uid }) {
    const w = 44;
    const h = 52;
    const x = 22;
    const path = `M${x},6 L${x},${h - 6}`;
    const gradId = `flow-v-grad-${uid}`;
    const filterId = `flow-v-glow-${uid}`;
    return (
        <svg width={w} height={h} viewBox={`0 0 ${w} ${h}`} className="home-hero-flow-svg home-hero-flow-svg--v" aria-hidden style={{ flexShrink: 0, display: 'block', margin: '2px auto' }}>
            <defs>
                <linearGradient id={gradId} x1="0%" y1="0%" x2="0%" y2="100%">
                    <stop offset="0%" stopColor="rgba(99, 102, 241, 0.2)" />
                    <stop offset="45%" stopColor="rgba(56, 189, 248, 0.85)" />
                    <stop offset="100%" stopColor="rgba(14, 165, 233, 0.35)" />
                </linearGradient>
                <filter id={filterId} x="-50%" y="-50%" width="200%" height="200%">
                    <feGaussianBlur stdDeviation="1.2" result="blur" />
                    <feMerge>
                        <feMergeNode in="blur" />
                        <feMergeNode in="SourceGraphic" />
                    </feMerge>
                </filter>
            </defs>
            <line
                x1={x}
                y1={6}
                x2={x}
                y2={h - 6}
                stroke={`url(#${gradId})`}
                strokeWidth="2.5"
                strokeLinecap="round"
                opacity="0.9"
            />
            {!reduceMotion ? (
                <circle r="3.5" fill="#a5f3fc" filter={`url(#${filterId})`}>
                    <animateMotion dur="2.2s" repeatCount="indefinite" path={path} />
                </circle>
            ) : null}
            {!reduceMotion ? (
                <circle r="2.5" fill="#38bdf8" opacity="0.88">
                    <animateMotion dur="2.2s" repeatCount="indefinite" begin="0.75s" path={path} />
                </circle>
            ) : null}
        </svg>
    );
}

/** Abstract heatmap tile (not a vendor logo) — reads as “viz / Tableau-style” reporting. */
function MiniTableauGraphic({ compact }) {
    const [uid] = useState(() => `tab-${Math.random().toString(36).slice(2, 9)}`);
    const w = compact ? 52 : 60;
    const h = compact ? 38 : 44;
    const cells = [
        0.35, 0.55, 0.25, 0.7, 0.45, 0.6,
        0.5, 0.3, 0.65, 0.4, 0.55, 0.35,
        0.42, 0.58, 0.38, 0.62, 0.48, 0.52,
    ];
    return (
        <svg width={w} height={h} viewBox="0 0 60 42" aria-hidden style={{ display: 'block' }}>
            <defs>
                <linearGradient id={`${uid}-teal`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#0d9488" />
                    <stop offset="100%" stopColor="#5eead4" />
                </linearGradient>
                <linearGradient id={`${uid}-coral`} x1="1" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#ea580c" />
                    <stop offset="100%" stopColor="#fb923c" />
                </linearGradient>
            </defs>
            <rect x="1" y="1" width="58" height="40" rx="7" fill="rgba(15, 23, 42, 0.72)" stroke="rgba(94, 234, 212, 0.35)" strokeWidth="1" />
            {cells.map((op, i) => {
                const col = i % 6;
                const row = Math.floor(i / 6);
                const cx = 5 + col * 8.2;
                const cy = 6 + row * 10;
                const useTeal = (col + row) % 2 === 0;
                return (
                    <rect
                        key={`${uid}-c-${i}`}
                        x={cx}
                        y={cy}
                        width="7"
                        height="8"
                        rx="1.5"
                        fill={useTeal ? `url(#${uid}-teal)` : `url(#${uid}-coral)`}
                        opacity={0.25 + op * 0.65}
                    />
                );
            })}
        </svg>
    );
}

/** Small dashboard frame (KPI strip + chart) — used instead of a generic chart glyph. */
function MiniDashboardGraphic({ compact }) {
    const [uid] = useState(() => `dash-${Math.random().toString(36).slice(2, 9)}`);
    const gidBars = `dash-bars-${uid}`;
    const gidLine = `dash-line-${uid}`;
    const w = compact ? 54 : 62;
    const h = compact ? 38 : 44;
    return (
        <svg width={w} height={h} viewBox="0 0 62 44" aria-hidden style={{ display: 'block' }}>
            <defs>
                <linearGradient id={gidBars} x1="0" y1="1" x2="0" y2="0">
                    <stop offset="0%" stopColor="#0ea5e9" />
                    <stop offset="100%" stopColor="#7dd3fc" />
                </linearGradient>
                <linearGradient id={gidLine} x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.2" />
                    <stop offset="50%" stopColor="#a5f3fc" stopOpacity="0.95" />
                    <stop offset="100%" stopColor="#38bdf8" stopOpacity="0.35" />
                </linearGradient>
            </defs>
            <rect x="1" y="1" width="60" height="42" rx="7" fill="rgba(15, 23, 42, 0.72)" stroke="rgba(56, 189, 248, 0.4)" strokeWidth="1" />
            <rect x="5" y="5" width="52" height="5" rx="1.5" fill="rgba(148, 163, 184, 0.22)" />
            <rect x="5" y="13" width="16" height="9" rx="2" fill="rgba(56, 189, 248, 0.28)" />
            <rect x="23" y="13" width="16" height="9" rx="2" fill="rgba(99, 102, 241, 0.28)" />
            <rect x="41" y="13" width="16" height="9" rx="2" fill="rgba(34, 211, 238, 0.22)" />
            <rect x="6" y="27" width="5" height="12" rx="1.2" fill={`url(#${gidBars})`} />
            <rect x="13" y="24" width="5" height="15" rx="1.2" fill={`url(#${gidBars})`} opacity="0.92" />
            <rect x="20" y="29" width="5" height="10" rx="1.2" fill={`url(#${gidBars})`} opacity="0.78" />
            <rect x="27" y="26" width="5" height="13" rx="1.2" fill={`url(#${gidBars})`} opacity="0.88" />
            <path
                d="M 36 34 L 41 30 L 46 32 L 51 26 L 56 28"
                fill="none"
                stroke={`url(#${gidLine})`}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

/** `icon`: optional tile when no vendor logo (e.g. dashboard / Power BI on /reporting). */
function LogoCard({ src, label, delayClass, compact, icon }) {
    const sz = compact ? 34 : 40;
    const graphic =
        icon === 'powerbi' || icon === 'dashboard' ? (
            <MiniDashboardGraphic compact={compact} />
        ) : icon === 'tableau' ? (
            <MiniTableauGraphic compact={compact} />
        ) : icon === 'layers' ? (
            <LayersIcon style={{ fontSize: sz, color: '#7dd3fc' }} aria-hidden />
        ) : icon === 'sheets' ? (
            <TableChartIcon style={{ fontSize: sz, color: '#7dd3fc' }} aria-hidden />
        ) : (
            <img src={src} alt="" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
        );

    return (
        <Box
            className={`home-hero-flow-card ${delayClass || ''}`}
            role="img"
            aria-label={label}
            style={{
                width: compact ? 100 : 108,
                height: compact ? 70 : 76,
                borderRadius: compact ? 16 : 18,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: compact ? 10 : 12,
                boxSizing: 'border-box',
                flexShrink: 0,
            }}
        >
            {graphic}
        </Box>
    );
}

function floatClass(i) {
    const n = (i % 4) + 1;
    return `home-hero-flow-card--d${n}`;
}

/**
 * Premium hero data-flow graphic — rotates through the same scenarios as SMBHeroVisual.
 * @param {Array} [scenarios] — Optional slides `{ sources, destinations, headline, subtext, ctaTo, ctaLabel? }`.
 *   Destination rows may use `{ label, logo }` or `{ label, icon: 'dashboard' | 'powerbi' | 'tableau' | 'layers' | 'sheets' }`.
 * @param {string} [previewLabel]
 * @param {string} [ctaLinkLabel] — Default link text under hub when slide has no `ctaLabel`.
 * @param {'sync'|'assessment'} [hubIcon]
 */
export default function HomeHeroFlowVisual({
    scenarios: scenariosProp,
    previewLabel = 'Source → destination',
    ctaLinkLabel = 'See how this works →',
    hubIcon = 'sync',
} = {}) {
    const theme = useTheme();
    const mdUp = useMediaQuery(theme.breakpoints.up('md'));
    const [reduceMotion, setReduceMotion] = useState(false);
    const [connIds] = useState(() => ({
        a: `a-${Math.random().toString(36).slice(2, 9)}`,
        b: `b-${Math.random().toString(36).slice(2, 9)}`,
        va: `va-${Math.random().toString(36).slice(2, 9)}`,
        vb: `vb-${Math.random().toString(36).slice(2, 9)}`,
    }));

    const defaultRotationStates = useMemo(
        () => [
            {
                sources: [
                    { label: 'Salesforce', logo: salesforceLogo },
                    { label: 'Meta Ads', logo: facebookLogo },
                ],
                destinations: [
                    { label: 'Google Sheets', logo: googlesheetsLogo },
                    { label: 'Dashboard', icon: 'dashboard' },
                ],
                headline: 'Your tools → the reports you use',
                subtext: 'Data lands in Sheets, Excel, or dashboards—not another app to check.',
                ctaTo: '/reporting-data-automation',
            },
            {
                sources: [
                    { label: 'Meta Ads', logo: facebookLogo },
                    { label: 'Shopify', logo: shopifyLogo },
                ],
                destinations: [
                    { label: 'Google Sheets', logo: googlesheetsLogo },
                    { label: 'Excel / CSV', logo: excelLogo },
                ],
                headline: 'Ad spend and results in one workbook',
                subtext: 'Fewer exports. Less copy/paste between CSVs.',
                ctaTo: '/ads-to-dashboard',
            },
            {
                sources: [
                    { label: 'Shopify', logo: shopifyLogo },
                    { label: 'Salesforce', logo: salesforceLogo },
                    { label: 'Meta Ads', logo: facebookLogo },
                ],
                destinations: [
                    { label: 'Excel / CSV', logo: excelLogo },
                    { label: 'Google Sheets', logo: googlesheetsLogo },
                ],
                headline: 'Same spreadsheet—up-to-date numbers',
                subtext: 'We do not replace your file. Weekly totals updated for you.',
                ctaTo: '/reporting-data-automation',
            },
        ],
        [],
    );

    const rotationStates = scenariosProp ?? defaultRotationStates;

    const [slideIndex, setSlideIndex] = useState(0);
    const [contentOpacity, setContentOpacity] = useState(1);
    const [hoverPause, setHoverPause] = useState(false);

    const slide = rotationStates[slideIndex];

    useEffect(() => {
        const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
        const fn = () => setReduceMotion(mq.matches);
        fn();
        mq.addEventListener('change', fn);
        return () => mq.removeEventListener('change', fn);
    }, []);

    useEffect(() => {
        if (reduceMotion || hoverPause) return undefined;
        const id = window.setInterval(() => {
            setContentOpacity(0);
            window.setTimeout(() => {
                setSlideIndex((i) => (i + 1) % rotationStates.length);
                setContentOpacity(1);
            }, TRANSITION_MS);
        }, ROTATE_MS);
        return () => window.clearInterval(id);
    }, [reduceMotion, hoverPause, rotationStates]);

    const goToSlide = (i) => {
        if (i === slideIndex) return;
        setContentOpacity(0);
        window.setTimeout(() => {
            setSlideIndex(i);
            setContentOpacity(1);
        }, TRANSITION_MS);
    };

    const hub = (
        <Box
            className="home-hero-flow-hub"
            style={
                mdUp
                    ? undefined
                    : {
                          width: '100%',
                          maxWidth: 318,
                          marginLeft: 'auto',
                          marginRight: 'auto',
                          boxSizing: 'border-box',
                      }
            }
        >
            <Box className="home-hero-flow-hub-icon" aria-hidden>
                {hubIcon === 'assessment' ? (
                    <AssessmentIcon style={{ fontSize: 26, color: '#0369a1' }} />
                ) : (
                    <AutorenewIcon style={{ fontSize: 26, color: '#0369a1' }} />
                )}
            </Box>
            <Typography
                component="p"
                style={{
                    fontWeight: 600,
                    fontSize: mdUp ? '17px' : '16px',
                    color: '#f8fafc',
                    lineHeight: 1.28,
                    letterSpacing: '-0.03em',
                    margin: 0,
                    textAlign: 'center',
                }}
            >
                {slide.headline}
            </Typography>
            <Typography
                component="p"
                style={{
                    fontSize: '12px',
                    color: '#94a3b8',
                    marginTop: 10,
                    lineHeight: 1.55,
                    marginBottom: 0,
                    textAlign: 'center',
                    maxWidth: mdUp ? 220 : 280,
                }}
            >
                {slide.subtext}
            </Typography>
            <NavLink
                to={preserveTrackingParams(slide.ctaTo)}
                style={{
                    marginTop: 14,
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#7dd3fc',
                    textDecoration: 'none',
                    letterSpacing: '-0.01em',
                }}
            >
                {slide.ctaLabel ?? ctaLinkLabel}
            </NavLink>
        </Box>
    );

    const rotatingStage = (
        <>
            {mdUp ? (
                <Box className="home-hero-flow-stage">
                    <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 14, flex: '0 0 auto' }}>
                        {slide.sources.map((row, i) => (
                            <LogoCard
                                key={`${slideIndex}-s-${row.label}`}
                                src={row.logo}
                                label={row.label}
                                icon={row.icon}
                                delayClass={floatClass(i)}
                            />
                        ))}
                    </Box>
                    <Box display="flex" alignItems="center" style={{ padding: '0 2px' }}>
                        <FlowConnector flipped={false} reduceMotion={reduceMotion} uid={connIds.a} />
                    </Box>
                    <Box style={{ flex: '0 0 auto' }}>{hub}</Box>
                    <Box display="flex" alignItems="center" style={{ padding: '0 2px' }}>
                        <FlowConnector flipped reduceMotion={reduceMotion} uid={connIds.b} />
                    </Box>
                    <Box
                        display={slide.destinations.length >= 4 ? 'grid' : 'flex'}
                        flexDirection={slide.destinations.length >= 4 ? undefined : 'column'}
                        gridTemplateColumns={slide.destinations.length >= 4 ? 'repeat(2, auto)' : undefined}
                        alignItems={slide.destinations.length >= 4 ? 'stretch' : 'flex-start'}
                        style={{
                            gap: slide.destinations.length >= 4 ? 10 : 14,
                            flex: '0 0 auto',
                        }}
                    >
                        {slide.destinations.map((row, i) => {
                            const useGrid = slide.destinations.length >= 4;
                            const lastSpans = useGrid && slide.destinations.length % 2 === 1 && i === slide.destinations.length - 1;
                            return (
                                <Box
                                    key={`${slideIndex}-d-${row.label}`}
                                    style={lastSpans ? { gridColumn: '1 / -1', justifySelf: 'center' } : undefined}
                                >
                                    <LogoCard
                                        src={row.logo}
                                        label={row.label}
                                        icon={row.icon}
                                        delayClass={floatClass(i + 2)}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            ) : (
                <Box className="home-hero-flow-mobile-stack" display="flex" flexDirection="column" alignItems="center" style={{ gap: 6, width: '100%' }}>
                    <Box display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: 10, rowGap: 10 }}>
                        {slide.sources.map((row, i) => (
                            <LogoCard
                                key={`${slideIndex}-sm-s-${row.label}`}
                                src={row.logo}
                                label={row.label}
                                icon={row.icon}
                                delayClass={floatClass(i)}
                                compact
                            />
                        ))}
                    </Box>
                    <FlowConnectorVertical reduceMotion={reduceMotion} uid={connIds.va} />
                    {hub}
                    <FlowConnectorVertical reduceMotion={reduceMotion} uid={connIds.vb} />
                    <Box display="flex" justifyContent="center" flexWrap="wrap" style={{ gap: 10, rowGap: 10 }}>
                        {slide.destinations.map((row, i) => (
                            <LogoCard
                                key={`${slideIndex}-sm-d-${row.label}`}
                                src={row.logo}
                                label={row.label}
                                icon={row.icon}
                                delayClass={floatClass(i + 2)}
                                compact
                            />
                        ))}
                    </Box>
                </Box>
            )}
        </>
    );

    return (
        <Box
            component="figure"
            className="home-hero-flow-root"
            aria-label={`Data flow: ${slide.headline}`}
            style={{ margin: 0 }}
            onMouseEnter={() => setHoverPause(true)}
            onMouseLeave={() => setHoverPause(false)}
            onTouchStart={() => setHoverPause(true)}
            onTouchEnd={() => setHoverPause(false)}
        >
            <style>{`
        .home-hero-flow-root {
          position: relative;
          width: 100%;
          min-height: ${mdUp ? (slide.destinations.length >= 4 ? 400 : 320) : 0}px;
        }
        .home-hero-flow-mobile-stack {
          max-width: 360px;
          margin-left: auto;
          margin-right: auto;
          padding: 0 6px;
          box-sizing: border-box;
        }
        @media (max-width: 959px) {
          .home-hero-flow-hub {
            max-width: none !important;
          }
          .home-hero-flow-phantom {
            opacity: 0.26 !important;
            filter: blur(30px) !important;
          }
          .home-hero-flow-phantom--2 {
            opacity: 0.18 !important;
          }
        }
        .home-hero-flow-phantom {
          position: absolute;
          border-radius: 999px;
          pointer-events: none;
          z-index: 0;
          opacity: 0.42;
          filter: blur(22px);
          transform: translate3d(12px, 18px, 0) scale(0.92);
          background: linear-gradient(145deg, rgba(56, 189, 248, 0.22), rgba(99, 102, 241, 0.16));
          box-shadow: 0 40px 80px rgba(0, 0, 0, 0.25);
          mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, black 0%, transparent 72%);
          -webkit-mask-image: radial-gradient(ellipse 70% 70% at 50% 50%, black 0%, transparent 72%);
        }
        .home-hero-flow-phantom--2 {
          transform: translate3d(-18px, 28px, 0) scale(0.88);
          opacity: 0.3;
        }
        @keyframes home-hero-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-5px); }
        }
        .home-hero-flow-card {
          background: linear-gradient(165deg, rgba(255,255,255,0.09) 0%, rgba(255,255,255,0.04) 100%);
          border: 1px solid rgba(255, 255, 255, 0.1);
          box-shadow:
            0 4px 24px rgba(0, 0, 0, 0.25),
            inset 0 1px 0 rgba(255, 255, 255, 0.12);
          backdrop-filter: blur(14px);
          -webkit-backdrop-filter: blur(14px);
          transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
        }
        .home-hero-flow-card:hover {
          transform: translateY(-4px);
          border-color: rgba(125, 211, 252, 0.28);
          box-shadow:
            0 12px 40px rgba(0, 0, 0, 0.35),
            0 0 32px -8px rgba(56, 189, 248, 0.2),
            inset 0 1px 0 rgba(255, 255, 255, 0.14);
        }
        .home-hero-flow-card.home-hero-flow-card--d1 {
          animation: home-hero-float 5s ease-in-out infinite;
        }
        .home-hero-flow-card.home-hero-flow-card--d2 {
          animation: home-hero-float 5.8s ease-in-out infinite;
          animation-delay: -1.2s;
        }
        .home-hero-flow-card.home-hero-flow-card--d3 {
          animation: home-hero-float 6.2s ease-in-out infinite;
          animation-delay: -2.4s;
        }
        .home-hero-flow-card.home-hero-flow-card--d4 {
          animation: home-hero-float 5.4s ease-in-out infinite;
          animation-delay: -0.8s;
        }
        @media (prefers-reduced-motion: reduce) {
          .home-hero-flow-card.home-hero-flow-card--d1,
          .home-hero-flow-card.home-hero-flow-card--d2,
          .home-hero-flow-card.home-hero-flow-card--d3,
          .home-hero-flow-card.home-hero-flow-card--d4 {
            animation: none !important;
          }
        }
        .home-hero-flow-hub {
          position: relative;
          z-index: 2;
          text-align: center;
          max-width: 268px;
          margin: 0 auto;
          padding: 26px 22px 24px;
          border-radius: 22px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: linear-gradient(165deg, rgba(255,255,255,0.12) 0%, rgba(255,255,255,0.05) 45%, rgba(15,23,42,0.35) 100%);
          backdrop-filter: blur(22px) saturate(1.2);
          -webkit-backdrop-filter: blur(22px) saturate(1.2);
          box-shadow:
            0 20px 56px rgba(0, 0, 0, 0.45),
            inset 0 1px 0 rgba(255, 255, 255, 0.14),
            0 0 60px -12px rgba(56, 189, 248, 0.22);
          transition: transform 0.28s ease, box-shadow 0.28s ease;
        }
        .home-hero-flow-hub:hover {
          transform: translateY(-3px);
          box-shadow:
            0 28px 64px rgba(0, 0, 0, 0.5),
            inset 0 1px 0 rgba(255, 255, 255, 0.16),
            0 0 72px -8px rgba(56, 189, 248, 0.28);
        }
        .home-hero-flow-hub-icon {
          width: 48px;
          height: 48px;
          margin: 0 auto 14px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          background: linear-gradient(160deg, rgba(224,242,254,0.95) 0%, rgba(186,230,253,0.75) 100%);
          box-shadow: inset 0 1px 1px rgba(255,255,255,0.7), 0 8px 24px rgba(14, 165, 233, 0.25);
        }
        .home-hero-flow-stage {
          position: relative;
          z-index: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 4px;
          flex-wrap: wrap;
          padding-top: 8px;
        }
        @media (min-width: 960px) {
          .home-hero-flow-stage {
            flex-wrap: nowrap;
            gap: 2px;
          }
        }
      `}</style>

            <Box className="home-hero-flow-phantom" style={{ width: '88%', height: 200, top: 24, left: '6%' }} aria-hidden />
            <Box className="home-hero-flow-phantom home-hero-flow-phantom--2" style={{ width: '70%', height: 160, top: 80, left: '12%' }} aria-hidden />

            <Typography
                component="p"
                style={{
                    fontSize: mdUp ? '13px' : '12px',
                    fontWeight: 600,
                    letterSpacing: '0.06em',
                    textTransform: 'uppercase',
                    color: '#64748b',
                    marginBottom: 14,
                    textAlign: mdUp ? 'left' : 'center',
                }}
            >
                {previewLabel}
            </Typography>

            <Box
                aria-live={reduceMotion ? 'off' : 'polite'}
                aria-atomic="true"
                style={{
                    position: 'relative',
                    zIndex: 1,
                    opacity: contentOpacity,
                    transition: reduceMotion ? 'none' : `opacity ${TRANSITION_MS}ms ease`,
                }}
            >
                {rotatingStage}
            </Box>
            {!mdUp ? (
                <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    style={{ gap: 8, marginTop: 16, paddingBottom: 4, flexWrap: 'wrap' }}
                    role="tablist"
                    aria-label="Preview scenarios"
                >
                    {rotationStates.map((_, i) => (
                        <button
                            key={i}
                            type="button"
                            role="tab"
                            aria-selected={i === slideIndex}
                            aria-label={`Scenario ${i + 1} of ${rotationStates.length}`}
                            onClick={() => goToSlide(i)}
                            style={{
                                width: i === slideIndex ? 24 : 8,
                                height: 8,
                                borderRadius: 999,
                                border: 'none',
                                padding: 0,
                                cursor: 'pointer',
                                flexShrink: 0,
                                transition: 'width 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
                                background:
                                    i === slideIndex
                                        ? 'linear-gradient(90deg, #7dd3fc, #38bdf8)'
                                        : 'rgba(71, 85, 105, 0.95)',
                                boxShadow: i === slideIndex ? '0 0 14px rgba(56, 189, 248, 0.45)' : 'none',
                            }}
                        />
                    ))}
                </Box>
            ) : null}
        </Box>
    );
}
